// The first screen of a college page for a paid-ad visitor (#4476; founder
// pick A of the ad-landing mocks, 2026-10-16: "the ad's question, then the
// check"). The visitor taps an ad that asked a question or quoted a fact; the
// page opens on that question, its answer, and the "Where do I stand" card,
// before any scrolling. Organic visitors keep the page as it is.
//
// Every figure in an answer is read from the page's own data file (the same
// sourced figures the page shows further down, which the KB agreement tests
// hold to the KB), never typed into these words. A fact the page does not
// hold makes that ad's answer fall back to the generic one; the generic one
// needs only the page's acceptance rate.
//
// One entry per ad, keyed by the utm_term the ad's final URL carries (the
// changesets under scripts/ads/ write it; adLandings.test.jsx pins every key
// they use and every figure their words quote against the page data).
// Search ads carry no utm_term, so they get the generic answer, which covers
// their ad groups' themes: the acceptance rate, the deadline, then the GPA
// check in the card below.
import { acceptanceRate, count } from '../data/colleges';

export const LAYOUT = 'a';
export const GENERIC = 'generic';

const lower = (s) => String(s || '').toLowerCase();
const shortOf = (c) => c.shortName || c.name;

// A fact is { text, source, where } or null when the page lacks it.
const FACTS = {
    rate: (c) => {
        const a = c.admission;
        if (!a || !(a.applied > 0) || !(a.admitted > 0)) return null;
        return {
            text: `${shortOf(c)} admitted ${acceptanceRate(c)}% of first-year applicants for ${lower(a.entering)}: ${count(a.admitted)} of ${count(a.applied)}.`,
            source: a.source, where: a.where,
        };
    },
    deadline: (c) => {
        const d = c.deadlines;
        if (!d || !d.headline || !d.headline.label || !d.headline.value) return null;
        return { text: `${d.headline.label}: ${d.headline.value}, for ${lower(d.entering)} entry.`, source: d.source };
    },
    testing: (c) => {
        const t = c.testing;
        if (!t || !t.line) return null;
        return { text: t.line, source: t.source };
    },
};

// A school or major's own row in the page's "by school or major" table.
function majorFact(c, rowName) {
    const m = c.byMajor;
    const r = m && Array.isArray(m.rows) ? m.rows.find((x) => x.name === rowName) : null;
    if (!r || !(r.applied > 0) || !(r.admitted >= 0) || r.rate === undefined) return null;
    return {
        text: `${shortOf(c)}'s ${r.name} admitted ${count(r.admitted)} of ${count(r.applied)} first-year applicants for ${lower(m.year)}, a ${r.rate}% rate. Every ${shortOf(c)} school's rate is further down this page.`,
        source: m.source, where: m.where,
    };
}

const UCLA = 'university-of-california-los-angeles';
const MICHIGAN = 'university-of-michigan-ann-arbor';

// question(c): the heading. facts(c): the answer's facts, in order; any null
// fact sends the visitor to the generic answer. close: the line that points
// at the card below.
export const AD_LANDINGS = {
    // Ad "UCLA ad" (paused 2026-10-15; its clicks may still arrive)
    'ucla-ad': {
        slug: UCLA,
        question: (c) => `Is a 3.8 enough for ${shortOf(c)}?`,
        facts: (c) => [FACTS.rate(c)],
        close: 'Check where your own grades put you.',
    },
    // Ad A, the stand card
    'ucla-stand-ad': {
        slug: UCLA,
        question: (c) => `Where do you stand at ${shortOf(c)}?`,
        facts: (c) => [FACTS.deadline(c), FACTS.rate(c)],
        close: 'Enter your grade, GPA and home state below to see.',
    },
    // Ad B; its line follows the founder's rewrite: "UCLA won't read SAT or
    // ACT scores. See where your grades put you." Only true at a test-blind
    // school, so it is shown only while the page says the school is blind.
    'ucla-weighs-ad': {
        slug: UCLA,
        question: (c) => (c.testing && c.testing.blind ? `${shortOf(c)} won't read SAT or ACT scores.` : null),
        facts: (c) => [FACTS.testing(c)],
        close: 'See where your grades put you.',
    },
    'ucla-nursing-short': {
        slug: UCLA,
        question: (c) => `How many first-years did ${shortOf(c)} Nursing admit?`,
        facts: (c) => [majorFact(c, 'School of Nursing')],
        close: 'Check where your own grades put you.',
    },
    'michigan-scores-short': {
        slug: MICHIGAN,
        question: (c) => `Will ${shortOf(c)} read your SAT or ACT?`,
        facts: (c) => [FACTS.testing(c)],
        close: 'Check where your grades put you.',
    },
};

const GENERIC_LANDING = {
    question: (c) => `How hard is it to get into ${shortOf(c)}?`,
    facts: (c) => [FACTS.rate(c), FACTS.deadline(c)],
    close: 'Check where your own grades put you.',
};

function build(key, def, c) {
    const question = def.question(c);
    const facts = def.facts(c);
    if (!question || !facts.length || facts.some((f) => !f || !c.sources || !c.sources[f.source])) return null;
    return { key, question, facts, close: def.close };
}

/** Every answer this school's page can show a paid visitor: the generic one
 * first, then each ad's that lands here and whose facts the page holds. An
 * empty list when the page cannot answer even the generic question. */
export function landingsFor(c) {
    if (!c) return [];
    const generic = build(GENERIC, GENERIC_LANDING, c);
    if (!generic) return [];
    const own = Object.entries(AD_LANDINGS)
        .filter(([, def]) => def.slug === c.slug)
        .map(([key, def]) => build(key, def, c))
        .filter(Boolean);
    return [generic, ...own];
}

/** The answer key a visitor with this utm_term sees on this school's page:
 * the ad's own when the page can show it, else the generic one. */
export function answerFor(c, term) {
    const keys = landingsFor(c).map((l) => l.key);
    return term && term !== GENERIC && keys.includes(term) ? term : GENERIC;
}
